import { Component, EventEmitter, Input, Output } from '@angular/core';
import { IOperator, IProiect } from '../../proiecte.interface';
import { helper } from 'src/environments/helper';
import { ProiecteService } from '../../proiecte.service';
import { NotificationService } from 'src/app/services/notification.service';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { Observable, finalize, map } from 'rxjs';
import { Generic } from 'src/app/interfaces/generic.interface';
import { IAddResponsabilPayload, IResponsabilForm } from './responsabili-proiect.interface';
import Swal from 'sweetalert2';
import { PermissionsService } from 'src/app/services/permissions.service';
import { CommonService } from 'src/app/services/common.service';

@Component({
    selector: 'app-responsabili-proiect',
    templateUrl: './responsabili-proiect.component.html',
    styleUrls: ['./responsabili-proiect.component.scss']
})
export class ResponsabiliProiectComponent {
    @Input() proiect!: IProiect;
    @Input() directiiActive!: Generic[];
    @Output() proiectChanged: EventEmitter<void> = new EventEmitter();

    errorTitle: string = helper.dialogConfig.headers.errorAtentie;
    errorIcon: string = helper.dialogConfig.icons.error;
    errorType: string = helper.dialogConfig.icons.error;
    successTitle: string = helper.dialogConfig.headers.success;
    successIcon: string = helper.dialogConfig.icons.success;
    successType: string = helper.dialogConfig.icons.success;
    helper = helper;

    isLoading = false;
    showEditField = false;
    passedProiectId!: number;
    responsabiliProiect!: IOperator[];
    form!: FormGroup;

    compartimenteActive$!: Observable<Generic[]>;
    responsabiliDepartament$!: Observable<Generic[]>;

    defaultParameter: { name: string; id: number } = {
        name: '--Alegeți--',
        id: -1,
    };

    constructor(
        private proiecteService: ProiecteService,
        private notificationService: NotificationService,
        private fb: FormBuilder,
        public permissionsService: PermissionsService,
        public commonService: CommonService,
    ) { }

    ngOnChanges(): void {
        this.passedProiectId = this.proiect.id_proiect;
        this.responsabiliProiect = this.proiect.responsibles;
    }

    ngOnInit(): void {
        this.form = this.fb.group({
            id_directie: new FormControl(-1, []),
            id_compartiment: new FormControl(-1, []),
            id_responsabil: new FormControl(-1, [Validators.required, Validators.min(0)]),
        })
    }

    submitForm() {
        this.isLoading = true;

        const {
            id_responsabil,
        } = this.form.value as IResponsabilForm;
        const payload: IAddResponsabilPayload = {
            id_user: id_responsabil,
        };

        this.proiecteService
            .addResponsabil(this.passedProiectId, payload)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Responsabil adăugat cu succes!',
                        this.successIcon
                    ).then(() => {
                        this.proiectChanged.emit();
                        this.closeEditField();
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    toggleEditField() {
        this.showEditField = !this.showEditField;
    }

    closeEditField() {
        this.showEditField = false;
    }

    onDirectieChange(directieId: number): void {
        if (directieId > 0) {
            this.loadCompartimenteActive(directieId);
            this.searchResponsabili();
        } else {
            this.loadCompartimenteActive();
            this.searchResponsabili();
        }
    }

    loadCompartimenteActive(directieId?: number) {
        this.isLoading = true;
        let compartimenteObservable$ = directieId
            ? this.commonService.getCompartimenteByDirectie(directieId)
            : this.commonService.getCompartimenteActive();

        this.compartimenteActive$ = compartimenteObservable$.pipe(
            map((results: any) => [this.defaultParameter, ...results]),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    searchResponsabili() {
        const id_directie = this.form.get('id_directie')?.value;
        const id_compartiment = this.form.get('id_compartiment')?.value;

        if (id_directie > 0 || id_compartiment > 0) {
            this.loadResponsabili(id_directie, id_compartiment);
        } else {
            this.responsabiliDepartament$ = new Observable<Generic[]>((observer) => {
                observer.next([{ name: '--Alegeți--', id: -1 }]);
                observer.complete();
            });
        }
    }

    loadResponsabili(id_directie: number, id_comapartiment: number) {
        this.isLoading = true;
        let responsabiliObservable$ = this.proiecteService.getResponsabiliByDirectie(id_directie, id_comapartiment);

        this.responsabiliDepartament$ = responsabiliObservable$.pipe(
            map((results: any) => {
                return [this.defaultParameter, ...results]
            }),
            finalize(() => {
                this.isLoading = false;
            })
        );
    }

    removeResponsabil(id_responsabil: number) {
        this.isLoading = true;

        this.proiecteService
            .removeResponsabil(id_responsabil)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Responsabil șters cu succes!',
                        this.successIcon
                    ).then(() => {
                        this.proiectChanged.emit();
                        this.closeEditField();
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }

    setPrimaryResponsabil(id_responsabil: number) {
        this.isLoading = true;

        this.proiecteService
            .setPrimaryResponsabil(id_responsabil)
            .pipe(finalize(() => (this.isLoading = false)))
            .subscribe({
                next: (res) => {
                    this.notificationService.warningSwal(
                        this.successTitle,
                        'Responsabilul este primar!',
                        this.successIcon
                    ).then(() => {
                        this.proiectChanged.emit();
                        this.closeEditField();
                    });
                },
                error: (err) => {
                    let msg;
                    try {
                        // try to catch the message from server
                        msg = err.error.errors.message[0];
                    } catch (error) {
                        // controll output message
                        msg = helper.dialogConfig.generalMessages.error;
                    }
                    this.notificationService.warningSwal(
                        this.errorTitle,
                        msg,
                        this.errorIcon
                    );
                },
            });
    }
}
